'use client'

import React, { useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { SegmentedControl } from '@kiao-voice/ui-kit'
import { setLocaleAction } from '@/app/actions/set-locale'
import type { WebLocale } from '@/lib/locale'

interface LanguageOption {
  value: WebLocale
  label: string
}

const OPTIONS: readonly LanguageOption[] = [
  { value: 'en', label: 'EN' },
  { value: 'zh-TW', label: '繁中' },
] as const

/**
 * Segmented EN / 繁中 toggle（共用 ui-kit SegmentedControl；選中段為實心墨黑）。
 * Clicking a segment:
 *   1. Calls the server action to persist the `kiao-ui-locale` cookie.
 *   2. Calls `router.refresh()` so the next render resolves the new locale
 *      on the server and flows it back through `I18nProvider`.
 *
 * We deliberately don't call `i18n.changeLanguage()` on the client. The
 * server is the single source of truth for the active locale — the client
 * just reflects it — which keeps SSR and the toggle state consistent.
 */
export function LanguageToggle() {
  const { t, i18n } = useTranslation(['common'])
  const router = useRouter()
  const [isPending, startTransition] = useTransition()

  const currentLocale = (i18n.language as WebLocale) ?? 'en'

  const handleSelect = (next: WebLocale) => {
    if (next === currentLocale || isPending) return
    startTransition(async () => {
      await setLocaleAction(next)
      router.refresh()
    })
  }

  return (
    <SegmentedControl
      aria-label={t('common:uiLanguage.switchTo')}
      value={currentLocale}
      variant="ink"
      options={OPTIONS}
      onValueChange={handleSelect}
      disabled={isPending}
    />
  )
}
